import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AttendanceDialogData,
  AttendanceDialogResult,
  AttendanceStatus,
} from '../../models/leap-v2-attendance.model';

@Component({
  selector: 'leap-v2-attendance-dialog',
  templateUrl: './leap-v2-attendance-dialog.component.html',
  styleUrls: ['./leap-v2-attendance-dialog.component.css'],
  standalone: false,
})
export class LeapV2AttendanceDialogComponent {
  reason = '';
  comments = '';
  parentCarerInformed = '';

  readonly reasons = ['Sick', 'Technical', 'Carer cancelled', 'Refused', 'Holiday', 'Other'];

  // Typed as AttendanceStatus[] on purpose. This list used to hold free strings
  // ('Cancelled - 24 hours notice', 'Cancelled - less than 24 hours notice') that had been renamed out
  // from under it, so the match never fired and a cancellation was never asked for a reason at all
  // (37492). Typing it means the next rename of AttendanceStatus breaks the build here instead of
  // quietly switching the question off.
  private static readonly REASON_STATUSES: AttendanceStatus[] = [
    'Late',
    'Cancelled - 1+ days notice',
    'Cancelled - same day',
  ];

  // Late is a note, not an exception — the reason dropdown already records why, and making the educator
  // write a comment on top of that was an overhead the business asked to drop (37232). Every other
  // status that opens this dialog carries a consequence and still needs one.
  private static readonly OPTIONAL_COMMENT_STATUSES: AttendanceStatus[] = ['Late'];

  get needsReason(): boolean {
    return LeapV2AttendanceDialogComponent.REASON_STATUSES.includes(this.data.status);
  }

  get needsComments(): boolean {
    return !LeapV2AttendanceDialogComponent.OPTIONAL_COMMENT_STATUSES.includes(this.data.status);
  }

  get needsSpoken(): boolean {
    return this.data.status === 'Absent';
  }

  get isValid(): boolean {
    const reasonOk = !this.needsReason || !!this.reason;
    const commentsOk = !this.needsComments || !!this.comments.trim();
    const spokenOk = !this.needsSpoken || !!this.parentCarerInformed;
    return reasonOk && commentsOk && spokenOk;
  }

  constructor(
    public dialogRef: MatDialogRef<LeapV2AttendanceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttendanceDialogData
  ) {}

  save(): void {
    if (!this.isValid) return;
    const result: AttendanceDialogResult = {
      reason: this.reason || undefined,
      comments: this.comments.trim(),
      parentCarerInformed: this.parentCarerInformed || undefined,
    };
    this.dialogRef.close(result);
  }
}
